/**
 * EditProfileModal
 *
 * Modal for editing a user's profile/role. Displays read-only user
 * information (name, email, entity) and a radio-button list of available
 * profiles. Uses React Hook Form + Zod for validation.
 *
 * ## Component Hierarchy
 * - **Level**: Organism
 * - **Feature**: Users
 * - **Location**: `src/features/users/components/ui/organisms/EditProfileModal/`
 *
 * ## Features
 * - Displays user name, email and entity (read-only)
 * - Shows radio-button list with available profiles
 * - Includes "Understand the roles" info button
 * - Submits role change to API
 *
 * @module features/users/components/ui/organisms/EditProfileModal
 */
import React, { useCallback, useEffect } from 'react';
import {
  App,
  Modal,
  Space,
  Typography,
  Radio,
  Spin,
  Input,
  Button as AntdButton,
} from 'antd';
import { QuestionCircleOutlined, DownOutlined } from '@ant-design/icons';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useLanguage } from '@/shared/hooks/useLanguage';
import type {
  BusinessAccountUser,
  ExternalProfile,
} from '@/features/users/types/users.types';
import { useExternalProfiles } from '@/features/users/hooks/useExternalProfiles';
import { useAssignRole } from '@/features/users/hooks/useUserRoles';
import { getProfileI18nKey } from '@/features/users/utils/profileLabels';
import { Button } from '@/shared/components/ui/atoms';

import './EditProfileModal.css';

export interface EditProfileModalProps {
  /** Whether the modal is visible */
  open: boolean;
  /** The user being edited */
  user: BusinessAccountUser | null;
  /** Business account name to display as entity */
  businessAccountName?: string;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Callback when edit is successful */
  onSuccess?: () => void;
  /** Callback to open profiles info modal */
  onOpenProfilesInfo?: () => void;
}

/** Zod schema – profile field must be a non-empty string */
const editProfileSchema = z.object({
  profile: z.string().min(1),
});

type EditProfileFormData = z.infer<typeof editProfileSchema>;

/**
 * EditProfileModal Component
 *
 * @param props - Component properties
 * @returns React component
 *
 * @example
 * ```tsx
 * <EditProfileModal
 *   open={isOpen}
 *   user={selectedUser}
 *   onClose={() => setIsOpen(false)}
 *   onSuccess={() => refetchUsers()}
 *   onOpenProfilesInfo={() => setProfilesInfoOpen(true)}
 * />
 * ```
 */
export const EditProfileModal: React.FC<EditProfileModalProps> = ({
  open,
  user,
  businessAccountName,
  onClose,
  onSuccess,
  onOpenProfilesInfo,
}) => {
  const { t } = useLanguage();
  const { notification } = App.useApp();

  const { data: profiles, isLoading: profilesLoading } = useExternalProfiles();
  const { mutateAsync: assignRole, isPending } = useAssignRole();

  const { control, handleSubmit, reset, watch } = useForm<EditProfileFormData>({
    resolver: zodResolver(editProfileSchema),
    defaultValues: { profile: '' },
  });

  const selectedRole = watch('profile');

  /** Reset form when user changes */
  useEffect(() => {
    if (user) {
      reset({ profile: user.profile?.role ?? '' });
    }
  }, [user, reset]);

  const handleFormSubmit = useCallback(
    async (data: EditProfileFormData) => {
      if (!user) return;

      try {
        await assignRole({
          userId: user.id,
          roleName: data.profile,
        });
        notification.success({
          message: t('Profile updated successfully'),
          placement: 'topRight',
        });
        onSuccess?.();
        onClose();
      } catch {
        notification.error({
          message: t('Failed to update profile'),
          placement: 'topRight',
        });
      }
    },
    [user, assignRole, notification, t, onSuccess, onClose]
  );

  return (
    <Modal
      open={open}
      width={700}
      title={t('Edit User')}
      onCancel={onClose}
      footer={
        <Space>
          <Button variant="ghost" fullWidth={false} onClick={onClose}>
            {t('Close')}
          </Button>
          <Button
            variant="primary"
            fullWidth={false}
            onClick={handleSubmit(handleFormSubmit)}
            loading={isPending}
            disabled={!selectedRole || selectedRole === user?.profile?.role}
          >
            {t('Save')}
          </Button>
        </Space>
      }
      destroyOnHidden
      className="edit-profile-modal"
      aria-label={t('Edit user profile modal')}
    >
      <div className="edit-profile-modal__content">
        {/* ── Read-only info fields ────────────────────────── */}
        <div className="edit-profile-modal__info-section">
          <div className="edit-profile-modal__field">
            <Typography.Text className="edit-profile-modal__label">
              {t('Name')}
            </Typography.Text>
            <Input
              readOnly
              value={user?.fullName ?? '—'}
              className="edit-profile-modal__input"
            />
          </div>

          <div className="edit-profile-modal__field">
            <Typography.Text className="edit-profile-modal__label">
              {t('Email')}
            </Typography.Text>
            <Input
              readOnly
              value={user?.email?.email ?? '—'}
              className="edit-profile-modal__input"
            />
          </div>

          <div className="edit-profile-modal__field">
            <Typography.Text className="edit-profile-modal__label">
              {t('Entity')}
            </Typography.Text>
            <Input
              readOnly
              value={businessAccountName ?? '—'}
              suffix={
                <DownOutlined className="edit-profile-modal__entity-icon" />
              }
              className="edit-profile-modal__input"
            />
          </div>
        </div>

        {/* ── Profile radio list ────────────────────────────── */}
        <div className="edit-profile-modal__profile-section">
          <div className="edit-profile-modal__profile-header">
            <Typography.Text className="edit-profile-modal__section-label">
              {t('User Profile')}
            </Typography.Text>
            {onOpenProfilesInfo && (
              <AntdButton
                type="link"
                icon={<QuestionCircleOutlined />}
                onClick={onOpenProfilesInfo}
                size="small"
                className="edit-profile-modal__info-btn"
              >
                {t('Understand the Roles')}
              </AntdButton>
            )}
          </div>

          {profilesLoading ? (
            <div className="edit-profile-modal__loading">
              <Spin size="small" />
            </div>
          ) : (
            <Controller
              name="profile"
              control={control}
              render={({ field }) => (
                <Radio.Group
                  value={field.value}
                  onChange={field.onChange}
                  className="edit-profile-modal__radio-group"
                >
                  {profiles?.map((profile: ExternalProfile) => (
                    <Radio key={profile.id} value={profile.role}>
                      {t(getProfileI18nKey(profile.name))}
                    </Radio>
                  ))}
                </Radio.Group>
              )}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default EditProfileModal;
